import _ from 'lodash';
import { createContext, useContext } from 'react';

export interface LegacyAccount {
	id: string;
	accessToken: string;
	refreshToken: string;
}

export interface Account extends LegacyAccount {
	userID: string;
	workspaceID: string;
	state?: Record<string, unknown>;
	updatedAt: number;
}

export class Keychain {
	public constructor(private readonly onChange: () => void) {}

	public add(
		id: { workspaceID: string; userID: string },
		{
			accessToken,
			refreshToken,
			state,
		}: { accessToken: string; refreshToken: string; state?: Record<string, unknown> },
		updatedAt = Date.now(),
	) {
		if (this.isLegacy()) {
			this.delete();
			this.add(id, { accessToken, refreshToken }, updatedAt);
		}

		if (this.isLegacy()) {
			return;
		}

		this.update((accounts) => ({
			...accounts,
			[this.key(id)]: {
				...id,
				accessToken,
				refreshToken,
				state,
				updatedAt,
			},
		}));
	}

	public switch(id: { workspaceID: string; userID: string }) {
		const key = this.key(id);

		this.update((accounts) => ({
			...accounts,
			[key]: {
				...accounts[key],
				updatedAt: Date.now(),
			},
		}));
	}

	public delete(id?: { workspaceID: string; userID: string } | Account | LegacyAccount) {
		if (this.isLegacy()) {
			localStorage.removeItem('token');
			localStorage.removeItem('refreshToken');
			localStorage.removeItem('id');

			this.onChange();
		} else if (id !== undefined && 'workspaceID' in id) {
			this.update((accounts) => _.omit(accounts, this.key(id)));
		} else {
			this.update(() => ({}));
		}
	}

	public all(): Account[] | LegacyAccount[] {
		if (this.isLegacy()) {
			return [
				{
					id: 'legacy:' + localStorage.getItem('id')!,
					accessToken: localStorage.getItem('token')!,
					refreshToken: localStorage.getItem('refreshToken')!,
				},
			];
		} else {
			const accounts = JSON.parse(localStorage.getItem('accounts') ?? '{}');
			const sorted = _.sortBy(
				Object.values(accounts) as Omit<Account, 'id'>[],
				(account) => -account.updatedAt,
			);
			return sorted.map((account) => ({
				id: this.key(account),
				...account,
			}));
		}
	}

	public find(id: string): Account | LegacyAccount | undefined {
		return this.all().find((account) => account.id === id);
	}

	public isLegacy() {
		return localStorage.getItem('id') !== null;
	}

	private key({ workspaceID, userID }: { workspaceID: string; userID: string }): string {
		return `${workspaceID}:${userID}`;
	}

	private update(
		mutate: (accounts: Record<string, Omit<Account, 'id'>>) => Record<string, Omit<Account, 'id'>>,
	) {
		const original = JSON.parse(localStorage.getItem('accounts') ?? '{}');
		const updated = JSON.stringify(mutate(original as Record<string, Account>));
		localStorage.setItem('accounts', updated);

		this.onChange();
	}
}

export const KeychainContext = createContext<Keychain>(new Keychain(() => {}));

export function useKeychain(): Keychain {
	const keychain = useContext(KeychainContext);
	return keychain;
}

export function useAccount(): Account | LegacyAccount {
	const accounts = useKeychain().all();

	if (accounts.length === 0) {
		throw new Error('User is not logged in.');
	}

	return accounts[0];
}
